import { CalendarDateProps } from '@src/components/reusable/Calendar';
import { Scheduling, RescheduleScheduling } from '@src/core/buyerApiClient';

export const weekdayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'] as const;

export const monthNames = [
  'january',
  'february',
  'march',
  'april',
  'may',
  'june',
  'july',
  'august',
  'september',
  'october',
  'november',
  'december',
] as const;

export type DateObj = [number, number, number];
export type MonthTuple = [number, number];

export const zeroPadd = (num: number): string => `0${num}`.slice(-2);

export const currentDate = (): DateObj => dateToObj(new Date());
export const dateToObj = (d: Date): DateObj => [d.getFullYear(), d.getMonth() + 1, d.getDate()];

export const initialMonth = (): MonthTuple => {
  const [year, month] = currentDate();
  return [year, month];
};

export const changeMonth = ([year, month]: MonthTuple, value: number): MonthTuple => {
  month += value;

  while (month <= 0) {
    year--;
    month += 12;
  }

  while (month >= 13) {
    year++;
    month -= 12;
  }

  return [year, month];
};

export const changeMonthByOne = (month: MonthTuple, value: number): MonthTuple => {
  value /= Math.abs(value);
  return changeMonth(month, value);
};

interface MountedIntervalCache {
  [key: string]: IntervalDate[];
}
const previouslyMountedIntervals: MountedIntervalCache = {};

export const mountInterval = (month: MonthTuple): IntervalDate[] => {
  const cacheKey = `${month[0]}-${month[1]}`;
  const cache = previouslyMountedIntervals[cacheKey];
  if (cache) return cache;

  const interval = [];
  let weekDay = monthWeekdayStart(month);

  if (weekDay > 0) {
    const previousMonth = changeMonthByOne(month, -1);
    let previousDate = monthTotalDays(previousMonth) - (weekDay - 1);
    for (let diff = weekDay; diff--; previousDate++) {
      interval.push(newIntervalDate(...previousMonth, previousDate));
    }
  }

  for (let d = 1, total = monthTotalDays(month); d <= total; d++) {
    interval.push(newIntervalDate(...month, d, true));
    weekDay = (weekDay + 1) % 7;
  }

  if (weekDay > 0) {
    const nextMonth = changeMonthByOne(month, 1);
    for (let d = 1; weekDay < 7; d++) {
      interval.push(newIntervalDate(...nextMonth, d));
      weekDay++;
    }
  }
  previouslyMountedIntervals[cacheKey] = interval;
  return interval;
};

export interface MergedIntervalWithDates {
  date: IntervalDate;
  availableSchedulings: Scheduling[] | RescheduleScheduling[] | undefined;
  unavailableSchedulings: Scheduling[] | RescheduleScheduling[] | undefined;
}

export const mergeIntervalWithDates = (
  interval: IntervalDate[],
  dates: CalendarDateProps,
): MergedIntervalWithDates[] => {
  return interval.map((date) => ({
    date,
    availableSchedulings: dates[date.dateString]?.availables,
    unavailableSchedulings: dates[date.dateString]?.unavailables,
  }));
};

export interface IntervalDate {
  year: number;
  month: number;
  date: number;
  dateString: string;
  highlight: boolean;
  when: When;
}

export const newIntervalDate = (year: number, month: number, date: number, highlight = false): IntervalDate => {
  const monthStr = zeroPadd(month);
  const dateStr = zeroPadd(date);
  const dateString = [year, monthStr, dateStr].join('-');
  const when = getDateInformation(year, month, date);
  return { year, month, date, dateString, highlight, when };
};

export const getDateInformation = (year: number, month: number, day: number) => {
  const date = new Date(year, month - 1, day);
  const [currentYear, currentMonth, currentDay] = currentDate();
  const today = new Date(currentYear, currentMonth - 1, currentDay);
  return date.getTime() === today.getTime() ? When.TODAY : date.getTime() < today.getTime() ? When.PAST : When.FUTURE;
};

export enum When {
  PAST,
  TODAY,
  FUTURE,
}
export const monthTotalDays = ([year, month]: MonthTuple): number => {
  switch (month) {
    case 4:
    case 6:
    case 9:
    case 11:
      return 30;
    case 2:
      return (year % 4 === 0 && year % 100 !== 0) || year % 400 === 0 ? 29 : 28;
    default:
      return 31;
  }
};

export const monthWeekdayStart = ([year, month]: MonthTuple): number => {
  const d = new Date(year, month - 1, 1);
  return d.getDay();
};

export const convertDatesToCalendarDates = (dates: { [key: string]: Scheduling[] | RescheduleScheduling[] }) => {
  return Object.entries(dates).reduce<CalendarDateProps>(
    (carry, [date, schedulings]) => ({
      ...carry,
      [date]: {
        availables: schedulings,
        unavailables: [],
      },
    }),
    {} as CalendarDateProps,
  );
};
